/* eslint-disable react/jsx-props-no-spreading */
import { forwardRef } from 'react';
// Utils & misc
import cn from '../../../utils/cn';
// Style
import style from './Input.module.css';
// Components
import Picto from '../../atoms/Picto/Picto';

/**
 * @typedef {object} InputProps
 * @property {keyof typeof import('../../atoms/Picto/Picto').availablePictos} [icon] - Icon name from available pictos
 * @property {boolean} [hasError] - Whether input has validation error
 * @property {boolean} [readOnly] - Read-only (posted, not user-editable)
 * @property {boolean} [isLiveSearch] - Primary border for live search criteria fields
 * @property {string} [className] - Additional CSS classes
 * @property {'default' | 'light'} [theme = 'default'] - Theme of the textfield
 */

/**
 * Input component - A text input field with optional icon
 * @param {InputProps & React.InputHTMLAttributes<HTMLInputElement>} props - Component props
 * @param {React.Ref<HTMLInputElement>} ref - Forwarded ref to the input element
 * @returns {JSX.Element} Input component
 */
function InputComponent({
  icon,
  hasError = false,
  readOnly = false,
  isLiveSearch = false,
  className,
  theme = 'default',
  ...props
}, ref) {
  const inputClassName = cn([
    style.input,
    style[theme],
    isLiveSearch && style['live-search'],
    hasError && style['has-error'],
    readOnly && style['read-only'],
    className,
  ]);

  return icon ? (
    <div className={icon ? `${style['has-icon']}` : null}>
      <input ref={ref} className={inputClassName} readOnly={readOnly} {...props} />
      <Picto icon={icon} className={style.icon} />
    </div>
  ) : <input ref={ref} className={inputClassName} readOnly={readOnly} {...props} />;
}

const Input = forwardRef(InputComponent);

export default Input;
